import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BackofficeLayout, { KpiCard, Card, fmtEUR } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { Wallet, TrendingUp, TrendingDown, Landmark, Building2, HardHat, AlertTriangle, FileSignature } from "lucide-react";

export default function BackofficeHome() {
  const [kpi, setKpi] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/backoffice/kpis/summary").then((r) => r.data),
      api.get("/backoffice/kpis/cash-flow-timeline?months=12").then((r) => r.data),
      api.get("/backoffice/leases/upcoming-revisions?days=90").then((r) => r.data).catch(() => []),
    ])
      .then(([k, t, u]) => {
        setKpi(k);
        setTimeline(t.data || []);
        setUpcoming(u);
      })
      .finally(() => setLoading(false));
  }, []);

  const g = kpi?.global || {};
  const perEntity = kpi?.per_entity || [];

  const maxCF = Math.max(1, ...timeline.map((d) => Math.max(Math.abs(d.revenu), Math.abs(d.charge + d.mensualite))));

  return (
    <BackofficeLayout
      title="Vue Groupe"
      subtitle="Consolidation temps réel : trésorerie, cash-flow, dette et projets du groupe."
    >
      {loading ? (
        <div className="text-slate-400" data-testid="bof-loading">Chargement…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="bof-global-kpis">
            <KpiCard label="Trésorerie totale" value={fmtEUR(g.tresorerie_totale)}
                     sub={`${g.nb_entites} entités`} tone="amber" testid="kpi-tresorerie" />
            <KpiCard label="Cash-flow mensuel" value={fmtEUR(g.cash_flow_mensuel_total)}
                     sub={`Revenus ${fmtEUR(g.revenus_mensuels_total)}`}
                     tone={g.cash_flow_mensuel_total >= 0 ? "green" : "red"}
                     testid="kpi-cash-flow" />
            <KpiCard label="Dette totale" value={fmtEUR(g.dette_totale)}
                     sub={`Débloqué ${fmtEUR(g.capital_debloque)} · Reste ${fmtEUR(g.reste_a_tirer)}`}
                     tone="slate" testid="kpi-dette" />
            <KpiCard label="Projets actifs" value={g.nb_projets_actifs}
                     sub={`Mensualité prêts ${fmtEUR(g.mensualite_prets_total)}`}
                     tone="blue" testid="kpi-projets" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="bof-leases-kpis-global">
            <KpiCard label="Baux locatifs actifs" value={g.nb_baux_actifs || 0}
                     sub="Contribuent au cash-flow" tone="green" testid="kpi-baux" />
            <KpiCard label="Loyer annuel HT total" value={fmtEUR(g.loyer_annuel_total)}
                     sub="Hors charges refacturées" tone="green" testid="kpi-loyer-annuel" />
            <KpiCard label="Taxe foncière annuelle" value={fmtEUR(g.taxe_fonciere_annuelle)}
                     sub="Cumul groupe" tone="red" testid="kpi-taxe-fonciere" />
          </div>

          <Card className="mb-8" data-testid="bof-timeline-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cash-flow — 12 derniers mois</div>
                <div className="text-sm text-slate-300 mt-1">Basé sur les transactions bancaires catégorisées</div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-emerald-400" /> Revenus</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-red-400" /> Charges</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-amber-400" /> Prêts</span>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 items-end h-48" data-testid="bof-timeline-chart">
              {timeline.map((m) => {
                const rev = (m.revenu / maxCF) * 100;
                const chg = (m.charge / maxCF) * 100;
                const men = (m.mensualite / maxCF) * 100;
                return (
                  <div key={m.month} className="flex flex-col items-center gap-1" title={`Cash-flow: ${fmtEUR(m.cash_flow)}`}>
                    <div className="w-full flex items-end gap-0.5 h-40">
                      <div className="flex-1 bg-emerald-400/80 rounded-sm" style={{ height: `${rev}%` }} />
                      <div className="flex-1 bg-red-400/80 rounded-sm" style={{ height: `${chg}%` }} />
                      <div className="flex-1 bg-amber-400/80 rounded-sm" style={{ height: `${men}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">{m.month.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          </Card>

          <div>
            <h2 className="text-lg font-bold mb-4 text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-400" /> Par entité
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="bof-per-entity">
              {perEntity.map((e) => (
                <Card key={e.entity_id} data-testid={`entity-card-${e.entity_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{e.entity_type}</div>
                      <div className="font-bold text-white mt-0.5">{e.name}</div>
                    </div>
                    <div className="w-8 h-8 rounded" style={{ background: e.color }} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <Metric label="Trésorerie" value={fmtEUR(e.tresorerie)} icon={<Wallet className="w-3.5 h-3.5" />} />
                    <Metric label="Cash-flow / mois" value={fmtEUR(e.cash_flow_mensuel)}
                            icon={e.cash_flow_mensuel >= 0 ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> : <TrendingDown className="w-3.5 h-3.5 text-red-400" />} />
                    <Metric label="Revenus / mois" value={fmtEUR(e.revenus_mensuels)} />
                    <Metric label="Charges / mois" value={fmtEUR(e.charges_mensuelles)} />
                    <Metric label="Mensualité prêts" value={fmtEUR(e.mensualite_prets)} icon={<Landmark className="w-3.5 h-3.5" />} />
                    <Metric label="Dette totale" value={fmtEUR(e.dette_totale)} />
                    <Metric label="Reste à tirer" value={fmtEUR(e.reste_a_tirer)} />
                    <Metric label="Projets" value={e.nb_projets} icon={<HardHat className="w-3.5 h-3.5" />} />
                    <Metric label="Baux actifs" value={e.nb_baux_actifs || 0} />
                    <Metric label="Loyer annuel" value={fmtEUR(e.loyer_annuel_total || 0)} />
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {g.cash_flow_mensuel_total < 0 && (
            <div className="mt-8 p-4 bg-red-500/10 border border-red-800 rounded flex items-start gap-3" data-testid="bof-alert">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-red-300">Cash-flow groupe négatif</div>
                <div className="text-xs text-red-200/80 mt-1">
                  Le total revenus mensuels — charges — mensualités de prêt est inférieur à 0.
                  Renforcez les revenus locatifs ou renégociez les prêts.
                </div>
              </div>
            </div>
          )}

          {/* Baux à réviser prochainement */}
          {upcoming.length > 0 && (
            <div className="mt-8" data-testid="bof-upcoming-revisions">
              <h2 className="text-lg font-bold mb-4 text-white flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-amber-400" />
                Baux à réviser prochainement ({upcoming.length})
              </h2>
              <Card>
                <div className="space-y-2">
                  {upcoming.map((u) => {
                    const tone = u.urgency === "critical" ? "bg-red-500/10 border-red-800 text-red-200" :
                                 u.urgency === "high" ? "bg-amber-500/10 border-amber-700 text-amber-200" :
                                 u.urgency === "medium" ? "bg-sky-500/10 border-sky-700 text-sky-200" :
                                 "bg-slate-800/60 border-slate-700 text-slate-300";
                    const dayLabel = u.is_overdue
                      ? `⚠ En retard de ${Math.abs(u.days_until)} jours`
                      : u.days_until === 0
                        ? "Aujourd'hui"
                        : `Dans ${u.days_until} jours`;
                    return (
                      <Link to="/backoffice-finance/baux" key={u.id}
                            className={`flex items-center justify-between gap-4 p-3 rounded border ${tone} hover:opacity-80 transition-opacity`}
                            data-testid={`bof-upcoming-${u.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-white truncate">{u.tenant_name}</div>
                          <div className="text-xs opacity-70 truncate">{u.property_label || u.entity_name}</div>
                        </div>
                        <div className="text-right text-xs shrink-0">
                          <div className="font-mono font-bold">{u.next_review_date}</div>
                          <div className="opacity-80">{dayLabel}</div>
                        </div>
                        <div className="text-right text-xs shrink-0">
                          <div className="text-slate-400">Loyer actuel</div>
                          <div className="font-mono font-bold">{fmtEUR(u.current_rent)}</div>
                        </div>
                        <div className="text-right text-xs shrink-0">
                          <div className="text-slate-400">Indice ref</div>
                          <div className="font-mono">{u.reference_index || "—"} <span className="opacity-60">{u.reference_quarter}</span></div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  💡 Cliquez sur un bail pour ouvrir la page des baux, puis utilisez le bouton &quot;Réviser&quot;.
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </BackofficeLayout>
  );
}

const Metric = ({ label, value, icon }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
      {icon}
      {label}
    </div>
    <div className="text-sm font-bold text-white mt-1 truncate">{value}</div>
  </div>
);
